import axios from "axios";
import { resolveDealLostTaskClosureConfirmation } from "@/Utile/ncrmTerminalTaskClosure.mjs";

export const dealLostReasonMethods = {
  handleEditStageChange(value) {
    this.editForm.stage_id = Number(value || 0) || null;
    this.maybeOpenWonHandoffFromEdit({
      nextStageId: this.editForm.stage_id,
    });
    this.maybeOpenLostReasonFromEdit({
      nextStageId: this.editForm.stage_id,
    });
  },
  handleEditStatusChange(value) {
    this.editForm.status = String(value || "open").trim().toLowerCase() || "open";
    this.maybeOpenWonHandoffFromEdit({
      nextStatus: this.editForm.status,
    });
    this.maybeOpenLostReasonFromEdit({
      nextStatus: this.editForm.status,
    });
  },
  async applyStageInline(stageId) {
    const normalizedStageId = Number(stageId || 0) || null;
    if (!this.dealId || !this.deal || !normalizedStageId || this.inlineStageLoading) return;
    if (normalizedStageId === this.currentStageId) return;

    const nextStatus = this.resolveStatusFromStageId(normalizedStageId);
    const currentStatus = String(this.deal?.status || "open").trim().toLowerCase();
    if (nextStatus === "won" && nextStatus !== currentStatus) {
      this.openWonHandoffDialogFromEdit({
        targetStageId: normalizedStageId,
        reopenEditOnCancel: false,
      });
      return;
    }
    if (nextStatus === "lost" && nextStatus !== currentStatus) {
      const closeOpenActivities = this.resolveLostDealTaskClosureConfirmation({
        targetStageId: normalizedStageId,
        targetStatus: nextStatus,
      });
      if (closeOpenActivities === null) return;
      this.openLostReasonDialogFromEdit({
        targetStageId: normalizedStageId,
        targetStatus: nextStatus,
        dealPatch: {
          name: this.deal?.name || "",
          owner_user_id: Number(this.deal?.owner_user_id || 0) || null,
          expected_value: this.deal?.expected_value ?? null,
          expected_close_date: this.deal?.expected_close_date || null,
          ncrm_company_id: Number(this.deal?.ncrm_company_id || 0) || null,
          ncrm_person_id: Number(this.deal?.ncrm_person_id || 0) || null,
          notes: this.deal?.notes || null,
        },
        reopenEditOnCancel: false,
        closeOpenActivities,
      });
      return;
    }
    if (!this.ensureActiveTaskBeforeStageChange({ targetStageId: normalizedStageId })) return;

    this.inlineStageLoading = true;
    try {
      await axios.patch(`/ncrm/deals/${this.dealId}/stage`, {
        stage_id: normalizedStageId,
      });
      this.showSnackbar(this.$t("ncrm.deals.details.messages.stageChanged"), "success");
      await this.loadDeal();
    } catch (error) {
      console.error("[ncrm-deal-details] quick stage change error", error);
      this.showSnackbar(
        error?.response?.data?.error || this.$t("ncrm.deals.details.messages.stageChangeError"),
        "error"
      );
    } finally {
      this.inlineStageLoading = false;
    }
  },
  maybeOpenLostReasonFromEdit({
    nextStatus = this.editForm?.status,
    nextStageId = this.editForm?.stage_id,
  } = {}) {
    if (!this.editDialog || this.lostReasonDialog || this.wonHandoffDialog || !this.deal) return;

    const currentStatus = String(this.deal?.status || "open").trim().toLowerCase();
    const currentStageId = Number(this.deal?.stage_id || 0) || null;
    const currentStageType = this.resolveStageTypeById(currentStageId);
    const requestedStatus = String(nextStatus || "open").trim().toLowerCase();
    const requestedStageId = Number(nextStageId || 0) || null;
    const requestedStageType = this.resolveStageTypeById(requestedStageId);
    const isCurrentlyLost = currentStatus === "lost" || currentStageType === "lost";
    const statusTransitionsToLost = requestedStatus === "lost" && currentStatus !== "lost";
    const stageTransitionsToLost =
      requestedStageId &&
      requestedStageId !== currentStageId &&
      requestedStageType === "lost" &&
      !isCurrentlyLost;

    if (!statusTransitionsToLost && !stageTransitionsToLost) return;

    const closeOpenActivities = this.resolveLostDealTaskClosureConfirmation({
      targetStageId: stageTransitionsToLost ? requestedStageId : null,
      targetStatus: "lost",
    });
    if (closeOpenActivities === null) {
      this.resetEditLostSelection({ currentStatus, currentStageId });
      return;
    }

    const selection = this.resolveEditProductSelection(this.editForm?.product_ids);
    const expectedValue = this.estimateEditDealExpectedValue({
      selectedLineItemIds: selection.selectedExistingLineItemIds,
      productIdsToAttach: selection.productIdsToAttach,
    });

    this.openLostReasonDialogFromEdit({
      targetStageId: stageTransitionsToLost ? requestedStageId : null,
      targetStatus: "lost",
      dealPatch: {
        name: this.editForm.name,
        owner_user_id: this.editForm.owner_user_id,
        expected_value: expectedValue,
        expected_close_date: this.editForm.expected_close_date || null,
        ncrm_company_id: Number(this.editForm?.ncrm_company_id || 0) || null,
        ncrm_person_id: Number(this.editForm?.ncrm_person_id || 0) || null,
        notes: this.editForm.notes || null,
      },
      productIdsToAttach: selection.productIdsToAttach,
      reopenEditOnCancel: true,
      closeOpenActivities,
    });
  },
  resetEditLostSelection({ currentStatus = "open", currentStageId = null } = {}) {
    if (!this.editForm || typeof this.editForm !== "object") return;
    this.editForm.status = String(currentStatus || "open").trim().toLowerCase() || "open";
    this.editForm.stage_id = Number(currentStageId || 0) || null;
  },
  resolveLostDealTaskClosureConfirmation({
    targetStageId = null,
    targetStatus = "lost",
  } = {}) {
    const normalizedTargetStageId = Number(targetStageId || 0) || null;
    const targetStage =
      normalizedTargetStageId && typeof this.resolveStageTypeById === "function"
        ? {
            stage_type: this.resolveStageTypeById(normalizedTargetStageId),
          }
        : {
            stage_type: targetStatus,
          };
    return resolveDealLostTaskClosureConfirmation({
      stage: targetStage,
      record: this.deal,
      translate: this.$t,
      entityLabel: this.deal?.name || "",
    });
  },
  openLostReasonDialogFromEdit({
    targetStageId = null,
    targetStatus = "lost",
    dealPatch = null,
    productIdsToAttach = [],
    reopenEditOnCancel = false,
    closeOpenActivities = false,
  } = {}) {
    this.pendingLostReasonRequest = {
      targetStageId: Number(targetStageId || 0) || null,
      targetStatus: String(targetStatus || "lost").trim().toLowerCase() || "lost",
      dealPatch: dealPatch && typeof dealPatch === "object" && !Array.isArray(dealPatch) ? { ...dealPatch } : null,
      productIdsToAttach: Array.isArray(productIdsToAttach) ? [...productIdsToAttach] : [],
      reopenEditOnCancel: Boolean(reopenEditOnCancel),
      closeOpenActivities: Boolean(closeOpenActivities),
    };
    this.editDialog = false;
    this.lostReasonDialog = true;
  },
  handleLostReasonCancelled() {
    const shouldReopenEdit = Boolean(this.pendingLostReasonRequest?.reopenEditOnCancel);
    this.pendingLostReasonRequest = null;
    if (shouldReopenEdit) {
      this.editDialog = true;
    }
  },
  async handleLostReasonConfirm(reason) {
    if (!this.dealId || !this.pendingLostReasonRequest) return;
    const normalizedReason = String(reason || "").trim();
    if (!normalizedReason) return;

    this.lostReasonLoading = true;
    try {
      const request = this.pendingLostReasonRequest;
      await this.applyDealEditChanges({
        dealPatch: request.dealPatch,
        targetStageId: request.targetStageId,
        targetStatus: request.targetStatus,
        productIdsToAttach: request.productIdsToAttach,
        reason: normalizedReason,
        closeOpenActivities: request.closeOpenActivities === true,
      });
      this.lostReasonDialog = false;
      this.pendingLostReasonRequest = null;
      this.showSnackbar(this.$t("ncrm.deals.details.messages.updated"), "success");
      await this.loadDeal();
      await this.refreshDealActivityTimelineIfOpen();
    } catch (error) {
      console.error("[ncrm-deal-details] lost reason save error", error);
      this.showSnackbar(
        error?.response?.data?.error || this.$t("ncrm.deals.details.messages.updateError"),
        "error"
      );
    } finally {
      this.lostReasonLoading = false;
    }
  },
  async applyDealEditChanges({
    dealPatch = null,
    targetStageId = null,
    targetStatus = "",
    productIdsToAttach = [],
    reason = null,
    closeOpenActivities = false,
  } = {}) {
    if (!this.dealId || !this.deal) return;

    const normalizedDealPatch =
      dealPatch && typeof dealPatch === "object" && !Array.isArray(dealPatch) ? { ...dealPatch } : {};
    const normalizedProductIds = Array.isArray(productIdsToAttach)
      ? productIdsToAttach.map((item) => Number(item || 0)).filter((item) => item > 0)
      : [];
    const normalizedTargetStageId = Number(targetStageId || 0) || null;
    const normalizedTargetStatus = String(targetStatus || "").trim().toLowerCase();
    const normalizedReason = String(reason || "").trim() || null;
    const shouldCloseOpenActivities = closeOpenActivities === true;

    await axios.patch(`/ncrm/deals/${this.dealId}`, normalizedDealPatch);

    if (normalizedProductIds.length) {
      const attachResult = await this.attachProductsToDeal({
        dealId: this.dealId,
        productIds: normalizedProductIds,
      });
      if (attachResult.failedCount > 0) {
        this.showSnackbar(
          this.$t("ncrm.deals.details.messages.productsAttachPartial", {
            failed: attachResult.failedCount,
          }),
          "warning"
        );
      }
    }

    const effectiveStatusAfterStageMove = normalizedTargetStageId
      ? this.resolveStatusFromStageId(normalizedTargetStageId)
      : String(this.deal.status || "open").toLowerCase();

    if (normalizedTargetStageId) {
      const stagePayload = {
        stage_id: normalizedTargetStageId,
        reason: normalizedReason,
      };
      if (shouldCloseOpenActivities && effectiveStatusAfterStageMove === "lost") {
        stagePayload.close_open_activities = true;
      }
      await axios.patch(`/ncrm/deals/${this.dealId}/stage`, stagePayload);
    }

    if (normalizedTargetStatus && normalizedTargetStatus !== effectiveStatusAfterStageMove) {
      const statusPayload = {
        status: normalizedTargetStatus,
        reason: normalizedReason,
      };
      if (shouldCloseOpenActivities && normalizedTargetStatus === "lost") {
        statusPayload.close_open_activities = true;
      }
      await axios.patch(`/ncrm/deals/${this.dealId}/status`, statusPayload);
    }
  },
};
